import { useState, useCallback, useEffect } from 'react';
import { useThree, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { useAppStore, type Entity } from '../../state/store';
import { GROUND_PLANE } from '../../core/scene';
import { deleteSceneObjects, copySceneObjects } from '../../core/scene/editRouter';
import { notify } from '../../utils/notify';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { undoableAdd, undoableReplace } from '../../core/commands/entityOps';
import {
    commitEntityEdit,
    translateData,
    rotateData,
    scaleData,
    mirrorData,
    cloneEntityData,
    translateMatrix,
    rotateZMatrix,
    scaleMatrix,
    mirrorMatrix,
} from './entityGeometry';

/**
 * CAD Modify command tools (Move/Copy/Rotate/Scale/Mirror/Offset/Trim/
 * Extend/Delete/Join/Explode) for the drafting workbenches.
 *
 * These are the command-line counterparts of the uppercase modify tools:
 * typing MOVE / M / _MOVE resolves (via the preset layer or the legacy
 * parser) to the same activeTool id, and these scopes execute it against
 * CAD entities in the shared Z-up world frame. Selection-first, two-point
 * interaction, rubber-band preview, every completion is one undoable step.
 */

/** Shared pick-plane raycast for all two-point tools. */
function useGroundPicker() {
    const { raycaster, pointer, camera } = useThree();
    return useCallback((): THREE.Vector3 | null => {
        raycaster.setFromCamera(pointer, camera);
        const target = new THREE.Vector3();
        return raycaster.ray.intersectPlane(GROUND_PLANE, target) ? target : null;
    }, [raycaster, pointer, camera]);
}

/** Require a non-empty selection; notify when empty.
 *  PICKFIRST sysvar parity: 0 = pre-selection is ignored; the user must
 *  select objects after starting the command (classic 2D behavior). */
function useSelectedIds(): () => string[] {
    return useCallback(() => {
        const { selectedIds } = useAppStore.getState();
        if (useSystemVarsStore.getState().PICKFIRST === 0) {
            notify('PICKFIRST is off — select objects after starting the command.', 'warning');
            return [];
        }
        if (selectedIds.length === 0) {
            notify('Select objects first.', 'warning');
            return [];
        }
        return selectedIds;
    }, []);
}

// ------------------------------------------------------------------ MOVE
// eslint-disable-next-line react-refresh/only-export-components
export function useMoveTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    const requireIds = useSelectedIds();
    const [base, setBase] = useState<THREE.Vector3 | null>(null);
    const [preview, setPreview] = useState<THREE.Vector3 | null>(null);

    const isActive = activeTool === 'MOVE' || activeTool === 'move';

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { setBase(null); setPreview(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        if (!base) {
            requireIds(); // warn early if nothing selected
            setBase(p);
            setPreview(p);
        } else {
            const ids = requireIds();
            if (ids.length > 0) {
                const d: [number, number, number] = [p.x - base.x, p.y - base.y, p.z - base.z];
                commitEntityEdit(
                    ids,
                    (data) => translateData(data, d),
                    { matrix: translateMatrix(d), label: 'Move' }
                );
            }
            setBase(null);
            setPreview(null);
        }
    }, [isActive, getPoint, requireIds, base]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !base) return;
        const p = getPoint();
        // Sub-mm dedupe: pointermove fires far faster than the eye can see;
        // returning the previous reference lets React bail out of the render.
        if (p) setPreview((prev) => (prev && prev.distanceToSquared(p) < 1e-6 ? prev : p));
    }, [isActive, getPoint, base]);

    return { isActive, base, preview, handlePointerDown, handlePointerMove };
}

// ------------------------------------------------------------------ COPY
// eslint-disable-next-line react-refresh/only-export-components
export function useCopyTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    const requireIds = useSelectedIds();
    const [base, setBase] = useState<THREE.Vector3 | null>(null);
    const [preview, setPreview] = useState<THREE.Vector3 | null>(null);

    const isActive = activeTool === 'COPY' || activeTool === 'copy';

    // Escape aborts the staged base/displacement pick (same contract as Move).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isActive && e.key === 'Escape') { setBase(null); setPreview(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive]);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        if (!base) {
            requireIds();
            setBase(p);
            setPreview(p);
            return;
        }
        const ids = requireIds();
        if (ids.length > 0) {
            const d: [number, number, number] = [p.x - base.x, p.y - base.y, p.z - base.z];
            // B4 cross-workbench COPY: each owner clones its own citizens
            // (CAD entities, BIM elements, sketch solids/edges) with fresh
            // ids at the same shared-world offset; copies become the
            // selection regardless of their source.
            const newIds = copySceneObjects(ids, translateMatrix(d), 'Copy');
            if (newIds.length > 0) {
                useAppStore.getState().select(newIds);
                notify(`${newIds.length} object(s) copied.`, 'success');
            }
        }
        setBase(null);
        setPreview(null);
    }, [isActive, getPoint, requireIds, base]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !base) return;
        const p = getPoint();
        if (p) setPreview((prev) => (prev && prev.distanceToSquared(p) < 1e-6 ? prev : p));
    }, [isActive, getPoint, base]);

    return { isActive, base, preview, handlePointerDown, handlePointerMove };
}

// ----------------------------------------------------------------- ROTATE
// eslint-disable-next-line react-refresh/only-export-components
export function useRotateTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    const requireIds = useSelectedIds();
    const [center, setCenter] = useState<THREE.Vector3 | null>(null);
    const [ref, setRef] = useState<THREE.Vector3 | null>(null);
    const [preview, setPreview] = useState<THREE.Vector3 | null>(null);

    const isActive = activeTool === 'ROTATE' || activeTool === 'rotate';

    // Escape aborts the staged center/ref/angle picks.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isActive && e.key === 'Escape') { setCenter(null); setRef(null); setPreview(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive]);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        if (!center) {
            requireIds();
            setCenter(p);
        } else if (!ref) {
            setRef(p);
            setPreview(p);
        } else {
            const ids = requireIds();
            if (ids.length > 0) {
                const a0 = Math.atan2(ref.y - center.y, ref.x - center.x);
                const a1 = Math.atan2(p.y - center.y, p.x - center.x);
                const angle = a1 - a0;
                const c: [number, number] = [center.x, center.y];
                commitEntityEdit(
                    ids,
                    (data) => rotateData(data, c, angle),
                    { matrix: rotateZMatrix(c, angle), label: 'Rotate' }
                );
            }
            setCenter(null);
            setRef(null);
            setPreview(null);
        }
    }, [isActive, getPoint, requireIds, center, ref]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || center) {
            const p = getPoint();
            if (p) setPreview((prev) => (prev && prev.distanceToSquared(p) < 1e-6 ? prev : p));
        }
    }, [isActive, getPoint, center]);

    return { isActive, center, ref, preview, handlePointerDown, handlePointerMove };
}

// ------------------------------------------------------------------ SCALE
// eslint-disable-next-line react-refresh/only-export-components
export function useScaleTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    const requireIds = useSelectedIds();
    const [base, setBase] = useState<THREE.Vector3 | null>(null);
    const [ref, setRef] = useState<THREE.Vector3 | null>(null);
    const [preview, setPreview] = useState<THREE.Vector3 | null>(null);

    const isActive = activeTool === 'SCALE' || activeTool === 'scale';

    // Escape aborts the staged base/ref/factor picks.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isActive && e.key === 'Escape') { setBase(null); setRef(null); setPreview(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive]);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        if (!base) {
            requireIds();
            setBase(p);
        } else if (!ref) {
            setRef(p);
            setPreview(p);
        } else {
            const ids = requireIds();
            if (ids.length > 0) {
                const r0 = Math.max(base.distanceTo(ref), 1e-6);
                const factor = base.distanceTo(p) / r0;
                const b: [number, number] = [base.x, base.y];
                commitEntityEdit(
                    ids,
                    (data) => scaleData(data, b, factor),
                    { matrix: scaleMatrix(b, factor), label: 'Scale' }
                );
            }
            setBase(null);
            setRef(null);
            setPreview(null);
        }
    }, [isActive, getPoint, requireIds, base, ref]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !base) return;
        const p = getPoint();
        if (p) setPreview((prev) => (prev && prev.distanceToSquared(p) < 1e-6 ? prev : p));
    }, [isActive, getPoint, base]);

    return { isActive, base, ref, preview, handlePointerDown, handlePointerMove };
}

// ----------------------------------------------------------------- MIRROR
// eslint-disable-next-line react-refresh/only-export-components
export function useMirrorTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    const requireIds = useSelectedIds();
    const [p1, setP1] = useState<THREE.Vector3 | null>(null);
    const [preview, setPreview] = useState<THREE.Vector3 | null>(null);

    const isActive = activeTool === 'MIRROR_PICK' || activeTool === 'MIRROR' || activeTool === 'mirror';

    // Escape aborts the staged axis pick.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isActive && e.key === 'Escape') { setP1(null); setPreview(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isActive]);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        if (!p1) {
            requireIds();
            setP1(p);
            setPreview(p);
        } else {
            const ids = requireIds();
            if (ids.length > 0) {
                const a: [number, number] = [p1.x, p1.y];
                const b: [number, number] = [p.x, p.y];
                commitEntityEdit(
                    ids,
                    (data) => mirrorData(data, a, b),
                    { matrix: mirrorMatrix(a, b), label: 'Mirror' }
                );
            }
            setP1(null);
            setPreview(null);
        }
    }, [isActive, getPoint, requireIds, p1]);

    const handlePointerMove = useCallback(() => {
        if (!isActive || !p1) return;
        const p = getPoint();
        if (p) setPreview((prev) => (prev && prev.distanceToSquared(p) < 1e-6 ? prev : p));
    }, [isActive, getPoint, p1]);

    return { isActive, p1, preview, handlePointerDown, handlePointerMove };
}

// ----------------------------------------------------------------- OFFSET
// Select entity(ies) → OFFSET → click a side to place a parallel copy.
// A small default distance applies until a numeric distance is provided
// (kept local so no app-store field is needed for this first pass).
// eslint-disable-next-line react-refresh/only-export-components
export function useOffsetTool() {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();
    // Default offset distance; the clicked side point's perpendicular
    // distance overrides it (so typed distance entry — Dynamic Input feeds
    // a point along the cursor ray — controls the magnitude too).
    const [distance] = useState(1);

    const isActive = activeTool === 'OFFSET' || activeTool === 'offset';

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (!p) return;
        const ids = useAppStore.getState().selectedIds;
        if (ids.length === 0) { notify('Select objects to offset.', 'warning'); return; }
        const app = useAppStore.getState();
        const copies = ids
            .map((id) => app.entities[id])
            .filter(Boolean)
            .map((src) => {
                const newId = `cad:${crypto.randomUUID()}`;
                let data = cloneEntityData(src.data);
                if (src.type === 'circle' && Array.isArray(data.center) && typeof data.radius === 'number') {
                    // For circles the picked point defines the new radius.
                    const r = Math.hypot(p.x - data.center[0], p.y - data.center[1]);
                    data = { ...data, radius: Math.max(r, 1e-4) };
                } else {
                    const start = data.start as number[] | undefined;
                    const end = data.end as number[] | undefined;
                    if (start && end) {
                        const dir = new THREE.Vector2(end[0] - start[0], end[1] - start[1]).normalize();
                        const perp = new THREE.Vector2(-dir.y, dir.x);
                        const mid = new THREE.Vector2((start[0] + end[0]) / 2, (start[1] + end[1]) / 2);
                        const toPick = new THREE.Vector2(p.x - mid.x, p.y - mid.y);
                        const sign = toPick.dot(perp) >= 0 ? 1 : -1;
                        // The perpendicular distance from the picked side
                        // point to the source line is the offset magnitude;
                        // fall back to the default when the pick is degenerate.
                        const onLine = new THREE.Vector2(p.x - start[0], p.y - start[1]);
                        const d = Math.abs(onLine.dot(perp)) || distance;
                        data = translateData(data, [perp.x * d * sign, perp.y * d * sign, 0]);
                    } else {
                        data = translateData(data, [distance, 0, 0]);
                    }
                }
                return {
                    id: newId,
                    type: src.type,
                    name: `${src.name} Offset`,
                    visible: true,
                    locked: false,
                    layer: src.layer,
                    data,
                };
            });
        undoableAdd(copies);
        app.select(copies.map((c) => c.id));
        notify(`${copies.length} offset copy(ies) created.`, 'success');
    }, [isActive, getPoint, distance]);

    return { isActive, handlePointerDown, handlePointerMove: () => {} };
}

// ------------------------------------------------------------------ POINT
// eslint-disable-next-line react-refresh/only-export-components
export function usePointTool(multiple = false) {
    const activeTool = useAppStore((s) => s.activeTool);
    const getPoint = useGroundPicker();

    const isActive = activeTool === 'point' || activeTool === 'points'
        || (multiple && activeTool === 'POINT');

    const place = useCallback((p: THREE.Vector3) => {
        // Undoable: freshly placed points answer Ctrl+Z like every entity.
        undoableAdd([{
            id: `cad:${crypto.randomUUID()}`,
            type: 'point',
            name: `Point-${Date.now()}`,
            visible: true,
            locked: false,
            layer: useAppStore.getState().activeLayerId,
            data: { position: [p.x, p.y, p.z], color: '#ffff00' },
        }]);
    }, []);

    const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
        if (!isActive) return;
        e.stopPropagation();
        const p = getPoint();
        if (p) place(p);
    }, [isActive, getPoint, place]);

    return { isActive, handlePointerDown, handlePointerMove: () => {} };
}

/** Rubber-band helpers shared by the modify previews. */
// ------------------------------------------------------------- JOIN/EXPLODE
/** JOIN: merge selected line/polyline chains that share endpoints. */
// eslint-disable-next-line react-refresh/only-export-components
export function joinSelected(): void {
    const app = useAppStore.getState();
    const ids = app.selectedIds.filter((id) => {
        const t = app.entities[id]?.type;
        return t === 'line' || t === 'polyline';
    });
    if (ids.length < 2) { notify('Join needs at least two touching curves.', 'warning'); return; }

    const segs: { id: string; a: number[]; b: number[] }[] = [];
    ids.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        if (e.type === 'line') {
            segs.push({ id, a: [...e.data.start], b: [...e.data.end] });
        } else if (Array.isArray(e.data.vertices)) {
            const v = e.data.vertices as number[][];
            for (let i = 0; i < v.length - 1; i++) segs.push({ id, a: [...v[i]], b: [...v[i + 1]] });
        }
    });

    const chain: number[][] = [];
    const used = new Set<number>();
    const keyOf = (p: number[]) => `${p[0].toFixed(3)},${p[1].toFixed(3)},${(p[2] ?? 0).toFixed(3)}`;

    // Build chains greedily from an unused segment.
    for (let i = 0; i < segs.length; i++) {
        if (used.has(i)) continue;
        used.add(i);
        chain.push(segs[i].a, segs[i].b);
        let extended = true;
        while (extended) {
            extended = false;
            for (let j = 0; j < segs.length; j++) {
                if (used.has(j)) continue;
                const head = keyOf(chain[0]);
                const tail = keyOf(chain[chain.length - 1]);
                const a = keyOf(segs[j].a);
                const b = keyOf(segs[j].b);
                if (b === head) { chain.unshift(segs[j].a); used.add(j); extended = true; }
                else if (a === head) { chain.unshift(segs[j].b); used.add(j); extended = true; }
                else if (a === tail) { chain.push(segs[j].b); used.add(j); extended = true; }
                else if (b === tail) { chain.push(segs[j].a); used.add(j); extended = true; }
            }
        }
        if (chain.length < 3) { continue; }
        // Emit a joined polyline, delete sources — as one undoable step.
        const newId = `cad:${crypto.randomUUID()}`;
        undoableReplace(ids, [{
            id: newId,
            type: 'polyline',
            name: `Polyline-${Date.now()}`,
            visible: true,
            locked: false,
            layer: app.entities[ids[0]].layer,
            data: { vertices: chain.map((p) => [p[0], p[1], p[2] ?? 0]), closed: false },
        }]);
        return;
    }
    notify('Nothing connected to join.', 'warning');
}

/** EXPLODE: split a polyline/rectangle into constituent lines. */
// eslint-disable-next-line react-refresh/only-export-components
export function explodeSelected(): void {
    const app = useAppStore.getState();
    const ids = app.selectedIds;
    if (ids.length === 0) { notify('Select an object to explode.', 'warning'); return; }
    let produced = 0;
    ids.forEach((id) => {
        const e = app.entities[id];
        if (!e) return;
        const pts: number[][] = [];
        if (e.type === 'polyline' || e.type === 'polygon') {
            const v = e.data.vertices as number[][] | undefined;
            if (Array.isArray(v)) pts.push(...v);
            if (e.data.closed && pts.length > 1) pts.push(pts[0]);
        } else if (e.type === 'rectangle') {
            const c1 = e.data.corner1 as number[];
            const c2 = e.data.corner2 as number[];
            if (c1 && c2) {
                pts.push([c1[0], c1[1], 0], [c2[0], c1[1], 0], [c2[0], c2[1], 0], [c1[0], c2[1], 0], [c1[0], c1[1], 0]);
            }
        } else if (e.type === 'line') {
            return; // already simple
        }
        if (pts.length < 2) return;
        const segments: Entity[] = [];
        for (let i = 0; i < pts.length - 1; i++) {
            segments.push({
                id: `cad:${crypto.randomUUID()}`,
                type: 'line',
                name: `Line-${i}`,
                visible: true,
                locked: false,
                layer: e.layer,
                data: { start: pts[i], end: pts[i + 1], color: e.data.color ?? '#ffffff' },
            });
            produced++;
        }
        // ONE undoable step: delete the source, add the segments. Undo
        // restores the source and removes the segments; redo re-explodes.
        undoableReplace([id], segments);
    });
    if (produced === 0) notify('Selected object has nothing to explode.', 'warning');
    else notify(`Exploded into ${produced} line segment(s).`, 'success');
}

/** DELETE/ERASE: remove selected objects (undoable, cross-workbench).
 *  The selection is source-agnostic (A4): CAD entities, BIM elements and
 *  sketch solids are each erased through their owning store via the edit
 *  router, so an object drawn in one mode can be deleted from another. */
// eslint-disable-next-line react-refresh/only-export-components
export function eraseSelected(): void {
    const app = useAppStore.getState();
    const ids = app.selectedIds;
    if (ids.length === 0) { notify('Nothing selected to erase.', 'warning'); return; }
    const erased = deleteSceneObjects(ids, 'Erase');
    if (erased === 0) { notify('Selected objects cannot be erased.', 'warning'); return; }
    notify(`${erased} object(s) erased.`, 'success');
}


// ---------------------------------------------------------------- PREVIEW
/** DRAGMODE sysvar parity: 0 disables the dynamic rubber-band preview that
 *  accompanies drag/modify operations (the result still commits). */
function dragPreviewEnabled(): boolean {
    return useSystemVarsStore.getState().DRAGMODE !== 0;
}

/** Two-point rubber band (move/copy base->cursor). */
export function MovePreview({ base, current, color = '#00ff00' }: {
    base: THREE.Vector3 | null; current: THREE.Vector3 | null; color?: string;
}) {
    if (!base || !current || !dragPreviewEnabled()) return null;
    return (
        <Line points={[base, current]} color={color} lineWidth={1.5} dashed dashSize={0.2} gapSize={0.1} />
    );
}

/** Center + reference ray for rotate/scale previews. */
export function RotatePreview({ center, refPoint, previewPoint }: {
    center: THREE.Vector3 | null; refPoint: THREE.Vector3 | null; previewPoint: THREE.Vector3 | null;
}) {
    if (!center) return null;
    return (
        <group>
            <mesh position={center}>
                <sphereGeometry args={[0.06, 8, 8]} />
                <meshBasicMaterial color="#ffff00" />
            </mesh>
            {refPoint && <Line points={[center, refPoint]} color="#00ff00" lineWidth={1} />}
            {refPoint && previewPoint && (
                <Line points={[center, previewPoint]} color="#00ff00" lineWidth={1} dashed dashSize={0.2} gapSize={0.1} />
            )}
        </group>
    );
}

export const ScalePreview = RotatePreview;

/** Mirror axis preview line. */
export function MirrorPreview({ p1, p2 }: {
    p1: THREE.Vector3 | null; p2: THREE.Vector3 | null;
}) {
    if (!p1 || !p2) return null;
    // Extend the mirror line a bit beyond the picked points for clarity.
    const dir = p2.clone().sub(p1).normalize().multiplyScalar(2);
    return (
        <Line points={[p1.clone().sub(dir), p2.clone().add(dir)]} color="#00ff00" lineWidth={1} dashed dashSize={0.2} gapSize={0.1} />
    );
}

export const CopyPreview = MovePreview;
